import { z } from 'zod';
import { dateStringSchema } from './common.js';

/**
 * Academy calendar: holidays and teacher leave (migration 0020).
 *
 * All dates are inclusive `YYYY-MM-DD` strings in academy time
 * (Asia/Kolkata). Ranges are capped so a typo can't close the academy for a
 * year.
 */

export const MAX_HOLIDAY_DAYS = 62;
export const MAX_LEAVE_DAYS = 62;
export const MAX_CALENDAR_RANGE_DAYS = 120;

/** Inclusive day count between two `YYYY-MM-DD` dates (1 for a single day). */
export const inclusiveDays = (start: string, end: string): number =>
  Math.round((Date.parse(`${end}T00:00:00Z`) - Date.parse(`${start}T00:00:00Z`)) / 86_400_000) + 1;

export const holidayScopeSchema = z.enum(['all', 'online', 'offline', 'batch']);
export type HolidayScope = z.infer<typeof holidayScopeSchema>;

const holidayFields = {
  startDate: dateStringSchema,
  endDate: dateStringSchema,
  name: z.string().trim().min(2).max(120),
  scope: holidayScopeSchema.default('all'),
  batchId: z.string().uuid().nullable().optional(),
  notes: z.string().trim().max(1000).nullable().optional(),
};

/** Shared range + scope rules, applied to create and to the merged update. */
export const refineHoliday = <
  T extends { startDate: string; endDate: string; scope: HolidayScope; batchId?: string | null | undefined },
>(
  value: T,
  ctx: z.RefinementCtx,
): void => {
  if (value.endDate < value.startDate) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['endDate'], message: 'End date must be on or after the start date' });
  } else if (inclusiveDays(value.startDate, value.endDate) > MAX_HOLIDAY_DAYS) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['endDate'], message: `A holiday can span at most ${MAX_HOLIDAY_DAYS} days` });
  }
  if (value.scope === 'batch' && !value.batchId) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['batchId'], message: 'Choose the batch this holiday applies to' });
  }
  if (value.scope !== 'batch' && value.batchId) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['batchId'], message: 'Only a batch-scoped holiday can name a batch' });
  }
};

export const holidayCreateSchema = z.object(holidayFields).strict().superRefine(refineHoliday);
export type HolidayCreateInput = z.infer<typeof holidayCreateSchema>;

/** Partial update; the route merges it onto the stored row and re-checks with `holidayMergedSchema`. */
export const holidayUpdateSchema = z
  .object({
    startDate: holidayFields.startDate.optional(),
    endDate: holidayFields.endDate.optional(),
    name: holidayFields.name.optional(),
    scope: holidayScopeSchema.optional(),
    batchId: holidayFields.batchId,
    notes: holidayFields.notes,
  })
  .strict();
export type HolidayUpdateInput = z.infer<typeof holidayUpdateSchema>;

export const holidayMergedSchema = z
  .object({ ...holidayFields, scope: holidayScopeSchema })
  .superRefine(refineHoliday);

export const calendarRangeQuerySchema = z
  .object({ from: dateStringSchema, to: dateStringSchema })
  .superRefine((value, ctx) => {
    if (value.to < value.from) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['to'], message: '`to` must be on or after `from`' });
    } else if (inclusiveDays(value.from, value.to) > MAX_CALENDAR_RANGE_DAYS) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['to'], message: `Ask for at most ${MAX_CALENDAR_RANGE_DAYS} days at a time` });
    }
  });
export type CalendarRangeQuery = z.infer<typeof calendarRangeQuerySchema>;

export const leaveStatusSchema = z.enum(['requested', 'approved', 'rejected', 'cancelled']);
export type LeaveStatus = z.infer<typeof leaveStatusSchema>;
export const leaveTypeSchema = z.enum(['casual', 'sick', 'emergency', 'other']);
export type LeaveType = z.infer<typeof leaveTypeSchema>;

/**
 * Create leave. A teacher requests leave for themselves (`teacherId` is
 * ignored and taken from the session); an admin or nalim records leave for a
 * teacher, which is approved immediately.
 */
export const leaveCreateSchema = z
  .object({
    teacherId: z.string().uuid().optional(),
    startDate: dateStringSchema,
    endDate: dateStringSchema,
    leaveType: leaveTypeSchema.default('casual'),
    reason: z.string().trim().max(1000).nullable().optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.endDate < value.startDate) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['endDate'], message: 'End date must be on or after the start date' });
    } else if (inclusiveDays(value.startDate, value.endDate) > MAX_LEAVE_DAYS) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['endDate'], message: `Leave can span at most ${MAX_LEAVE_DAYS} days` });
    }
  });
export type LeaveCreateInput = z.infer<typeof leaveCreateSchema>;

export const leaveDecisionSchema = z
  .object({ note: z.string().trim().max(1000).nullable().optional() })
  .strict();
export type LeaveDecisionInput = z.infer<typeof leaveDecisionSchema>;

export const leaveListQuerySchema = z
  .object({
    teacherId: z.string().uuid().optional(),
    status: leaveStatusSchema.optional(),
    from: dateStringSchema.optional(),
    to: dateStringSchema.optional(),
  })
  .strict();
export type LeaveListQuery = z.infer<typeof leaveListQuerySchema>;

export interface HolidayDto {
  id: string;
  startDate: string;
  endDate: string;
  name: string;
  scope: HolidayScope;
  batchId: string | null;
  batchName: string | null;
  notes: string | null;
}

export interface LeaveDto {
  id: string;
  teacherId: string;
  teacherName: string;
  startDate: string;
  endDate: string;
  leaveType: LeaveType;
  reason: string | null;
  status: LeaveStatus;
  decidedBy: string | null;
  decidedAt: string | null;
  decisionNote: string | null;
  createdAt: string;
}

/** A class that differs from the timetable (cancelled, extra, make-up, or substituted). */
export interface CalendarSessionDto {
  id: string;
  batchId: string;
  batchName: string;
  scheduledAt: string;
  durationMinutes: number;
  kind: 'regular' | 'extra' | 'makeup';
  status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
  substituteTeacherId: string | null;
  cancelReason: string | null;
}

export interface CalendarRangeResponse {
  from: string;
  to: string;
  holidays: HolidayDto[];
  leaves: LeaveDto[];
  sessions: CalendarSessionDto[];
}
